import { useState, useMemo, useEffect } from "react";
import {
  Modal,
  TextInput,
  Stack,
  Select,
  MultiSelect,
  Paper,
  Group,
  Badge,
  Tooltip,
  Text,
  Box,
  Code,
  Button,
  Textarea,
  Divider,
  SegmentedControl,
  ScrollArea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import type { Editor } from "@tiptap/core";
import { IconPlus } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { useCreateDiagnosis } from "../../hooks/admin/useDiagnosis";
import { useDiagnosisCategoriesAdmin } from "../../hooks/admin/useDiagnosisCategories";
import { useCptCodesAdmin } from "../../hooks/admin/useCptCodes";
import { useIcdCodesAdmin } from "../../hooks/admin/useIcdCodes";
import {
  parseVariables,
  replaceVariables,
} from "../../services/internalGrossCode.service";
import { scrollToFirstError } from "../../utils/formValidation";
import { createClinicalRichTextExtensions } from "../shared/tiptap/clinicalRichTextExtensions";
import {
  CLINICAL_RICH_TEXT_COLORS,
  CLINICAL_RICH_TEXT_PARSE_OPTIONS,
} from "../../utils/clinicalRichText";

interface CreateMacroModalProps {
  opened: boolean;
  onClose: () => void;
  existingCodes: string[];
}

interface FormValues {
  code: string;
  diagnosisCategoryId: string;
  cptId: string;
  icdId: string;
  additionalIcdCodeIds: string[];
  diagnosisDescription: string;
  microscopicDescription: string;
  comments: string;
  scope: "global" | "personal";
}

const AVAILABLE_VARIABLES = [
  {
    name: "specimen_letter",
    description: "Specimen designation",
    example: "A",
  },
  { name: "source", description: "Tissue source", example: "Colon" },
  { name: "subsite", description: "Specific location", example: "Ascending" },
  {
    name: "location",
    description: "Full specimen location",
    example: "Colon, Ascending",
  },
  {
    name: "collection_method",
    description: "How collected",
    example: "Polypectomy",
  },
  { name: "size_cm", description: "Size in centimeters", example: "2.5" },
  { name: "block_count", description: "Number of blocks", example: "2" },
  { name: "color", description: "Tissue color", example: "tan-pink" },
  {
    name: "patient_name",
    description: "Patient full name",
    example: "John Smith",
  },
  {
    name: "case_number",
    description: "Case accession number",
    example: "26-G-00042",
  },
  {
    name: "date_collected",
    description: "Date specimen was collected",
    example: "01/15/2026",
  },
];

const SAMPLE_DATA: Record<string, string> = AVAILABLE_VARIABLES.reduce(
  (acc, v) => ({ ...acc, [v.name]: v.example }),
  {} as Record<string, string>
);

export default function CreateMacroModal({
  opened,
  onClose,
  existingCodes,
}: CreateMacroModalProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const createMutation = useCreateDiagnosis();
  const { data: categoriesRaw = [] } = useDiagnosisCategoriesAdmin();
  const { data: cptCodesRaw = [] } = useCptCodesAdmin();
  const { data: icdCodesRaw = [] } = useIcdCodesAdmin();
  const [showPreview, setShowPreview] = useState(false);

  const categories = useMemo(
    () =>
      categoriesRaw
        .map((c: any) => ({ value: c.id, label: c.name }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label)),
    [categoriesRaw]
  );

  const cptCodes = useMemo(
    () =>
      cptCodesRaw
        .filter((c: any) => c.isActive)
        .map((c: any) => ({
          value: c.id,
          label: `${c.code} - ${c.description}`,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label)),
    [cptCodesRaw]
  );

  const icdCodes = useMemo(
    () =>
      icdCodesRaw
        .filter((c: any) => c.isActive)
        .map((c: any) => ({
          value: c.id,
          label: `${c.code} - ${c.description}`,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label)),
    [icdCodesRaw]
  );

  const form = useForm<FormValues>({
    initialValues: {
      code: "",
      diagnosisCategoryId: "",
      cptId: "",
      icdId: "",
      additionalIcdCodeIds: [],
      diagnosisDescription: "",
      microscopicDescription: "",
      comments: "",
      scope: "personal",
    },
    validate: {
      code: (value) => {
        if (!value.trim()) return "Diagnosis code is required";
        if (
          existingCodes.some((c) => c.toLowerCase() === value.toLowerCase())
        ) {
          return "A macro with this code already exists";
        }
        return null;
      },
      diagnosisDescription: (value) =>
        !value.trim() ? "Diagnosis description is required" : null,
    },
  });

  const diagnosisEditor = useEditor({
    extensions: createClinicalRichTextExtensions(
      "Enter diagnosis template. Use {{variable_name}} for dynamic fields..."
    ),
    content: "",
    parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS,
    onUpdate: ({ editor: e }: { editor: Editor | null }) => {
      if (!e) return;
      form.setFieldValue("diagnosisDescription", e.getHTML());
    },
  }) as Editor | null;

  const microscopicEditor = useEditor({
    extensions: createClinicalRichTextExtensions(
      "Enter microscopic description template (optional)..."
    ),
    content: "",
    parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS,
    onUpdate: ({ editor: e }: { editor: Editor | null }) => {
      if (!e) return;
      form.setFieldValue("microscopicDescription", e.getHTML());
    },
  }) as Editor | null;

  useEffect(() => {
    if (opened) {
      form.reset();
      diagnosisEditor?.commands.clearContent();
      microscopicEditor?.commands.clearContent();
      setShowPreview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const insertVariable = (editor: Editor | null, name: string) => {
    editor?.chain().focus().insertContent(` {{${name}}} `).run();
  };

  const diagnosisVariables = form.values.diagnosisDescription
    ? parseVariables(form.values.diagnosisDescription)
    : [];

  const diagnosisPreview = useMemo(() => {
    if (!form.values.diagnosisDescription) return "";
    return replaceVariables(form.values.diagnosisDescription, SAMPLE_DATA);
  }, [form.values.diagnosisDescription]);

  const handleSubmit = async (values: FormValues) => {
    try {
      await createMutation.mutateAsync({
        code: values.code.trim(),
        diagnosisCategoryId: values.diagnosisCategoryId || "",
        cptId: values.cptId || "",
        icdId: values.icdId || "",
        additionalIcdCodeIds: values.additionalIcdCodeIds,
        diagnosisDescription: values.diagnosisDescription,
        microscopicDescription: values.microscopicDescription || null,
        comments: values.comments || null,
        isActive: true,
        scope: values.scope,
        createdById: values.scope === "personal" ? user?.id || null : null,
      });

      queryClient.invalidateQueries({ queryKey: ["diagnosis-macros"] });

      notifications.show({
        title: "Macro Created",
        message: `Macro "${values.code}" has been created`,
        color: "green",
      });

      onClose();
    } catch {
      // Error notification handled by the mutation hook
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      closeOnClickOutside={false}
      title={
        <Group gap="xs">
          <IconPlus size={18} />
          <Text fw={600}>Create Diagnosis Macro</Text>
        </Group>
      }
      size="lg"
      centered
      styles={{ body: { padding: 0 } }}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit, () => scrollToFirstError(form))}
      >
        <ScrollArea.Autosize
          mah="calc(100vh - 300px)"
          offsetScrollbars
          px="md"
          pt="md"
        >
          <Stack gap="md" pb="md">
            <SegmentedControl
              value={form.values.scope}
              onChange={(v) =>
                form.setFieldValue("scope", v as "global" | "personal")
              }
              data={[
                { label: "My Macro", value: "personal" },
                { label: "Global", value: "global" },
              ]}
              size="sm"
            />
            {form.values.scope === "personal" && (
              <Text size="xs" c="dimmed">
                Only you will see this macro in your diagnosis entry.
              </Text>
            )}
            {form.values.scope === "global" && (
              <Text size="xs" c="dimmed">
                All pathologists will see this macro.
              </Text>
            )}

            <TextInput
              label="Diagnosis Code"
              placeholder="e.g., BENIGN1"
              description="Short code for quick entry"
              withAsterisk
              {...form.getInputProps("code")}
            />
            <Select
              label="Category"
              placeholder="Select category"
              searchable
              clearable
              data={categories}
              {...form.getInputProps("diagnosisCategoryId")}
            />

            <Paper withBorder p="xs">
              <Text size="xs" fw={600} mb={4}>
                Variables (click to insert)
              </Text>
              <Group gap={4} wrap="wrap">
                {AVAILABLE_VARIABLES.map((v) => (
                  <Tooltip
                    key={v.name}
                    label={`${v.description} (e.g., "${v.example}")`}
                    withArrow
                  >
                    <Badge
                      variant="light"
                      color="indigo"
                      size="sm"
                      style={{ cursor: "pointer", fontFamily: "monospace" }}
                      onClick={() => insertVariable(diagnosisEditor, v.name)}
                    >
                      {`{{${v.name}}}`}
                    </Badge>
                  </Tooltip>
                ))}
              </Group>
            </Paper>

            <div>
              <Text component="label" fz="sm" fw={500} mb={5}>
                Diagnosis Description{" "}
                <Text span c="red">
                  *
                </Text>
              </Text>
              <RichTextEditor editor={diagnosisEditor}>
                <RichTextEditor.Toolbar sticky stickyOffset={60}>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Undo />
                    <RichTextEditor.Redo />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.ColorPicker
                      colors={CLINICAL_RICH_TEXT_COLORS}
                    />
                    <RichTextEditor.UnsetColor />
                    <RichTextEditor.Highlight />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.ClearFormatting />
                  </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>
                <RichTextEditor.Content style={{ minHeight: "120px" }} />
              </RichTextEditor>
              {form.errors.diagnosisDescription && (
                <Text size="xs" c="red" mt={5}>
                  {form.errors.diagnosisDescription}
                </Text>
              )}
            </div>

            {diagnosisVariables.length > 0 && (
              <Paper withBorder p="xs" bg="blue.0">
                <Group justify="space-between">
                  <Group gap="xs">
                    <Text size="xs" fw={600} c="blue">
                      Variables ({diagnosisVariables.length}):
                    </Text>
                    {diagnosisVariables.map((v) => (
                      <Code
                        key={v}
                        style={{
                          fontSize: "var(--mantine-font-size-xs)",
                        }}
                      >{`{{${v}}}`}</Code>
                    ))}
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-xs"
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    {showPreview ? "Hide Preview" : "Preview"}
                  </Button>
                </Group>
              </Paper>
            )}

            {showPreview && diagnosisPreview && (
              <Paper withBorder p="xs" bg="gray.0">
                <Text size="xs" fw={600} c="dimmed" mb={4}>
                  Preview (sample data):
                </Text>
                <Box
                  style={{ fontSize: "14px" }}
                  dangerouslySetInnerHTML={{ __html: diagnosisPreview }}
                />
              </Paper>
            )}

            <Divider label="Microscopic Description" labelPosition="center" />

            <div>
              <Text component="label" fz="sm" fw={500} mb={5}>
                Microscopic Description
              </Text>
              <Box mb="xs">
                <Group gap={4} wrap="wrap">
                  {AVAILABLE_VARIABLES.map((v) => (
                    <Tooltip key={v.name} label={`${v.description}`} withArrow>
                      <Badge
                        variant="light"
                        color="grape"
                        size="xs"
                        style={{ cursor: "pointer", fontFamily: "monospace" }}
                        onClick={() =>
                          insertVariable(microscopicEditor, v.name)
                        }
                      >
                        {`{{${v.name}}}`}
                      </Badge>
                    </Tooltip>
                  ))}
                </Group>
              </Box>
              <RichTextEditor editor={microscopicEditor}>
                <RichTextEditor.Toolbar sticky stickyOffset={60}>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Undo />
                    <RichTextEditor.Redo />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.ColorPicker
                      colors={CLINICAL_RICH_TEXT_COLORS}
                    />
                    <RichTextEditor.UnsetColor />
                    <RichTextEditor.Highlight />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.ClearFormatting />
                  </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>
                <RichTextEditor.Content style={{ minHeight: "80px" }} />
              </RichTextEditor>
            </div>

            <Divider label="Billing & Coding" labelPosition="center" />

            <Group grow align="flex-start">
              <Select
                label="CPT Code"
                placeholder="Select CPT code"
                searchable
                clearable
                data={cptCodes}
                {...form.getInputProps("cptId")}
              />
              <Select
                label="ICD Code"
                placeholder="Select ICD code"
                searchable
                clearable
                data={icdCodes}
                {...form.getInputProps("icdId")}
              />
            </Group>

            <MultiSelect
              label="Additional ICD Codes"
              placeholder={
                form.values.additionalIcdCodeIds.length === 0
                  ? "Select additional ICD codes"
                  : undefined
              }
              description="Extra ICD codes applied with this macro, in claim order after the primary"
              searchable
              clearable
              data={icdCodes}
              {...form.getInputProps("additionalIcdCodeIds")}
            />

            <Textarea
              label="Comments"
              placeholder="Internal notes (optional)"
              minRows={2}
              {...form.getInputProps("comments")}
            />
          </Stack>
        </ScrollArea.Autosize>

        <Box
          px="md"
          py="sm"
          style={{
            borderTop: "1px solid var(--mantine-color-default-border)",
          }}
        >
          <Group justify="space-between">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createMutation.isPending}
              leftSection={<IconPlus size={14} />}
            >
              Create Macro
            </Button>
          </Group>
        </Box>
      </form>
    </Modal>
  );
}
